import React from 'react';
import gql from 'graphql-tag';
import { useForm, FormProvider } from 'react-hook-form';
import { useMutation } from '@apollo/client';
import { useSelector } from 'react-redux';
import { injectIntl, defineMessages, FormattedMessage } from 'react-intl';
import { Icon } from 'semantic-ui-react';
import uuid from 'uuid';
import { map } from 'lodash';

import { Modal, Button } from '../../../components';
import CabinetForm from './form/CabinetForm';
import { GET_CABINETS_QUERY } from '../containers/CabinetList';

const intlMessages = defineMessages({
    defaultErrorMessage: {
        id: 'errors.defaultMessage',
        defaultMessage: 'Something went wrong',
    },
});

export const ADD_CABINET_MUTATION = gql`
    mutation createCabinet($input: CreateCabinetInput!, $lang: String!) {
        createCabinet(input: $input) {
            cabinet {
                id
                name(lang: $lang)
                nameVariants {
                    en
                    ru
                    lv
                }
                serviceGroups {
                    id
                    name(lang: $lang)
                    type
                }
            }
        }
    }
`;

function buildDefaultValues(languages) {
    const nameVariants = {};
    (languages || []).forEach(lang => {
        nameVariants[lang] = '';
    });
    return { nameVariants, serviceGroups: [] };
}

function AddCabinetDialog({ trigger, open, onClose, intl }) {
    const salon = useSelector(({ user }) => user.get('salon'));
    const languages = useSelector(({ intl: i }) => i.get('languages'));
    const defaultLanguage = useSelector(({ intl: i }) => i.get('defaultLanguage'));
    const currentLanguage = useSelector(({ intl: i }) => i.get('locale'));
    const searchQuery = useSelector(({ cabinets }) => cabinets?.get?.('searchQuery') || '');

    const methods = useForm({ defaultValues: buildDefaultValues(languages) });
    const { handleSubmit, reset, setError, formState: { isSubmitting, isDirty } } = methods;

    const [createCabinet] = useMutation(ADD_CABINET_MUTATION, {
        update: (store, { data: { createCabinet: result } }) => {
            try {
                const data = store.readQuery({
                    query: GET_CABINETS_QUERY,
                    variables: {
                        salon,
                        search: searchQuery || undefined,
                        lang: currentLanguage,
                    },
                });
                if (data?.viewer?.salon?.cabinets) {
                    store.writeQuery({
                        query: GET_CABINETS_QUERY,
                        variables: {
                            salon,
                            search: searchQuery || undefined,
                            lang: currentLanguage,
                        },
                        data: {
                            ...data,
                            viewer: {
                                ...data.viewer,
                                salon: {
                                    ...data.viewer.salon,
                                    cabinets: [...data.viewer.salon.cabinets, result.cabinet],
                                },
                            },
                        },
                    });
                }
            } catch (e) {
                // cache not populated — safe to ignore
            }
        },
    });

    const onModalClose = () => {
        reset(buildDefaultValues(languages));
        onClose();
    };

    const onSubmit = async formData => {
        try {
            await createCabinet({
                variables: {
                    input: { salon, ...formData },
                    lang: currentLanguage,
                },
                optimisticResponse: {
                    __typename: 'Mutation',
                    createCabinet: {
                        __typename: 'CreateCabinetPayload',
                        cabinet: {
                            __typename: 'Cabinet',
                            id: uuid.v4(),
                            name: formData.nameVariants[currentLanguage],
                            serviceGroups: map(formData.serviceGroups, id => ({
                                __typename: 'ServiceGroups',
                                id,
                                name: '',
                                type: '',
                            })),
                            nameVariants: formData.nameVariants,
                        },
                    },
                },
            });
            onModalClose();
        } catch (error) {
            const graphQLError = error.graphQLErrors && error.graphQLErrors[0];
            if (graphQLError) {
                setError('root', { message: graphQLError.message });
                return;
            }
            setError('root', { message: intl.formatMessage(intlMessages.defaultErrorMessage) });
        }
    };

    return (
        <FormProvider {...methods}>
            <Modal trigger={trigger} onClose={onModalClose} open={open}>
                <Modal.Header>
                    <FormattedMessage
                        id="pages.cabinets.AddCabinet.header"
                        defaultMessage="New cabinet"
                    />
                </Modal.Header>
                <Modal.Content>
                    <CabinetForm
                        languages={languages}
                        defaultLanguage={defaultLanguage}
                    />
                </Modal.Content>
                <Modal.Actions>
                    <Button
                        onClick={handleSubmit(onSubmit)}
                        disabled={isSubmitting || !isDirty}
                        loading={isSubmitting}
                        primary
                    >
                        <FormattedMessage
                            id="pages.cabinets.AddCabinet.save"
                            defaultMessage="Create"
                        />
                    </Button>
                    <Button onClick={onModalClose} disabled={isSubmitting} basic>
                        <Icon name="remove" />
                        <FormattedMessage
                            id="pages.cabinets.AddCabinet.cancel"
                            defaultMessage="Cancel"
                        />
                    </Button>
                </Modal.Actions>
            </Modal>
        </FormProvider>
    );
}

export default injectIntl(AddCabinetDialog);
